// 🔍 Servicio de OCR (Reconocimiento Óptico de Caracteres)
// Extrae texto de imágenes de boletas usando Tesseract.js

import { createWorker, Worker } from 'tesseract.js';

export interface OCRResult {
  text: string;
  confidence: number;
  extractedData?: {
    monto?: number;
    fecha?: string;
    descripcion?: string;
    categoria?: string;
  };
}

class OCRService {
  private worker: Worker | null = null;
  private isInitialized = false;

  /**
   * Inicializa el worker de Tesseract
   * Compatible con Tesseract.js v6.x
   * En v6.x, createWorker ya crea y carga el worker automáticamente
   */
  async initialize(): Promise<void> {
    if (this.isInitialized && this.worker) {
      return;
    }

    try {
      // Tesseract.js v6.x: createWorker ya crea y carga el worker automáticamente
      // No necesitas llamar a load() ni initialize()
      this.worker = await createWorker('spa');
      this.isInitialized = true;
      console.log('✅ OCR Service inicializado correctamente');
    } catch (error) {
      console.error('❌ Error al inicializar OCR Service:', error);
      this.worker = null;
      this.isInitialized = false;
      throw new Error(`No se pudo inicializar el servicio OCR: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    }
  }

  /**
   * Extrae texto de una imagen
   */
  async extractText(imageFile: File | Blob): Promise<OCRResult> {
    if (!this.isInitialized || !this.worker) {
      await this.initialize();
    }

    try {
      if (!this.worker) {
        throw new Error('Worker de OCR no inicializado');
      }

      console.log('🔍 Iniciando extracción de texto de la imagen...');
      const { data } = await this.worker.recognize(imageFile);
      
      const result: OCRResult = {
        text: data.text,
        confidence: data.confidence || 0
      };

      // Intentar extraer datos estructurados del texto
      result.extractedData = this.parseBoletaText(data.text);

      console.log('✅ Texto extraído:', result.text.substring(0, 200));
      console.log('📊 Confianza:', result.confidence);
      console.log('📋 Datos extraídos:', result.extractedData);

      return result;
    } catch (error) {
      console.error('❌ Error al extraer texto:', error);
      throw error;
    }
  }

  /**
   * Parsea el texto extraído para intentar identificar monto, fecha, descripción, etc.
   */
  private parseBoletaText(text: string): OCRResult['extractedData'] {
    const extracted: OCRResult['extractedData'] = {};
    const textLower = text.toLowerCase();

    // Buscar monto (formato: $123.456 o 123456 o 123.456)
    const montoPatterns = [
      /\$\s*([\d.,]+)/g,           // $123.456
      /total[:\s]*\$?\s*([\d.,]+)/gi, // Total: $123.456
      /monto[:\s]*\$?\s*([\d.,]+)/gi, // Monto: $123.456
      /([\d]{1,3}(?:\.[\d]{3})*(?:,[\d]+)?)\s*pesos/gi, // 123.456 pesos
      /([\d]{1,3}(?:\.[\d]{3})*(?:,[\d]+)?)\s*clp/gi     // 123.456 CLP
    ];

    for (const pattern of montoPatterns) {
      const match = text.match(pattern);
      if (match) {
        const montoStr = match[0]
          .replace(/[^\d.,]/g, '')
          .replace(/\./g, '')
          .replace(',', '.');
        const monto = parseFloat(montoStr);
        if (!isNaN(monto) && monto > 0) {
          extracted.monto = Math.round(monto);
          break;
        }
      }
    }

    // Buscar fecha (formato: DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY, YYYY/MM/DD)
    const fechaPatterns = [
      /(\d{4}[-/.]\d{1,2}[-/.]\d{1,2})/g,     // YYYY/MM/DD (primero este para detectarlo correctamente)
      /(\d{1,2}[-/.]\d{1,2}[-/.]\d{2,4})/g,   // DD/MM/YYYY
      /fecha[:\s]*(\d{1,2}[-/.]\d{1,2}[-/.]\d{2,4})/gi, // Fecha: DD/MM/YYYY
    ];

    for (const pattern of fechaPatterns) {
      const match = text.match(pattern);
      if (match) {
        const fechaStr = match[0];
        // Intentar convertir a formato ISO
        const fechaParts = fechaStr.split(/[-/.]/);
        if (fechaParts.length === 3) {
          let dia: string;
          let mes: string;
          let año: string;

          // Detectar formato: si la primera parte tiene 4 dígitos, es YYYY/MM/DD
          if (fechaParts[0].length === 4) {
            // Formato YYYY/MM/DD
            año = fechaParts[0];
            mes = fechaParts[1];
            dia = fechaParts[2];
          } else {
            // Formato DD/MM/YYYY
            dia = fechaParts[0];
            mes = fechaParts[1];
            año = fechaParts[2];
            
            // Si el año tiene 2 dígitos, asumir 20XX
            if (año.length === 2) {
              año = '20' + año;
            }
          }

          // Validar que el año sea razonable (entre 2000 y 2100)
          const añoNum = parseInt(año);
          if (añoNum >= 2000 && añoNum <= 2100) {
            // Formatear como YYYY-MM-DD
            extracted.fecha = `${año}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
            break;
          }
        }
      }
    }

    // Buscar descripción (líneas que no sean números o fechas)
    const lineas = text.split('\n').filter(line => {
      const lineTrim = line.trim();
      return lineTrim.length > 5 && 
             !lineTrim.match(/^\d+$/) && 
             !lineTrim.match(/^\d{1,2}[-/.]\d{1,2}[-/.]\d{2,4}$/) &&
             !lineTrim.toLowerCase().includes('total') &&
             !lineTrim.toLowerCase().includes('monto') &&
             !lineTrim.toLowerCase().includes('fecha');
    });

    if (lineas.length > 0) {
      // Tomar la primera línea que parezca una descripción
      extracted.descripcion = lineas[0].trim().substring(0, 200);
    }

    // Intentar identificar categoría basándose en palabras clave
    const categoriasKeywords: Record<string, string[]> = {
      'Alojamiento': ['hotel', 'hospedaje', 'alojamiento', 'hostal', 'residencia', 'apartamento', 'cabana', 'cabaña'],
      'Alimentación': ['almuerzo', 'comida', 'restaurant', 'restaurante', 'café', 'cafe', 'desayuno', 'cena', 'comida', 'alimentacion', 'alimentación'],
      'Combustible': ['combustible', 'gasolina', 'bencina', 'diesel', 'petroleo', 'petróleo', 'nafta'],
      'Viático': ['viatico', 'viático', 'gasto reservado', 'gastos reservados', 'viaticos', 'viáticos'],
      'Traslado': ['pasaje', 'pasajes', 'peaje', 'peajes', 'taxi', 'uber', 'transporte', 'traslado', 'bus', 'micro', 'avion', 'avión'],
      'Mantención de vehículos': ['mantencion', 'mantención', 'reparacion', 'reparación', 'vehiculo', 'vehículo', 'auto', 'moto', 'neumatico', 'neumático', 'aceite', 'filtro', 'bateria', 'batería'],
      'Materiales e insumos': ['papel', 'lapiz', 'lapices', 'impresora', 'toner', 'oficina', 'materiales', 'insumos', 'herramienta', 'herramientas', 'suministros'],
      'Bebestibles': ['bebida', 'bebidas', 'bebestible', 'bebestibles', 'agua', 'refresco', 'refrescos', 'jugo', 'jugos', 'cerveza', 'vino']
    };

    for (const [categoria, keywords] of Object.entries(categoriasKeywords)) {
      if (keywords.some(keyword => textLower.includes(keyword))) {
        extracted.categoria = categoria;
        break;
      }
    }

    return extracted;
  }

  /**
   * Termina el worker de OCR
   */
  async terminate(): Promise<void> {
    if (this.worker) {
      await this.worker.terminate();
      this.worker = null;
      this.isInitialized = false;
      console.log('✅ OCR Service terminado');
    }
  }
}

// Instancia singleton
export const ocrService = new OCRService();
